import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  merge,
  startWith,
  takeUntil,
} from 'rxjs';
import { SzamlazzhuService } from 'src/app/services/szamlazzhu/szamlazzhu.service';
import {
  GetInvoiceDetailsResponse,
  InvoicesDataTableItem,
} from 'src/app/services/szamlazzhu/types';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DecimalPipe, DatePipe } from '@angular/common';
import { InvoiceNumberFilterComponent } from './invoice-number-filter/invoice-number-filter.component';
import { InvoiceClientNameFilterComponent } from './invoice-client-name-filter/invoice-client-name-filter.component';
import { InvoiceClientTaxNumberFilterComponent } from './invoice-client-tax-number-filter/invoice-client-tax-number-filter.component';
import { InvoiceDebtorNameFilterComponent } from './invoice-debtor-name-filter/invoice-debtor-name-filter.component';
import { InvoiceDebtorTaxNumberFilterComponent } from './invoice-debtor-tax-number-filter/invoice-debtor-tax-number-filter.component';

@Component({
  selector: 'app-invoices-table',
  templateUrl: './invoices-table.component.html',
  styleUrls: ['./invoices-table.component.scss'],
  imports: [
    InvoiceNumberFilterComponent,
    InvoiceClientNameFilterComponent,
    InvoiceClientTaxNumberFilterComponent,
    InvoiceDebtorNameFilterComponent,
    InvoiceDebtorTaxNumberFilterComponent,
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DecimalPipe,
    DatePipe,
  ],
})
export class InvoicesTableComponent implements OnInit, OnDestroy {
  private szamlazzhuService = inject(SzamlazzhuService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private next: string | null = null;
  private prev: string | null = null;
  private cursor: string | null = null;
  private invoiceDetails = new Map<
    InvoicesDataTableItem,
    GetInvoiceDetailsResponse
  >();
  private readonly setDataSubject = new Subject<void>();
  private readonly destroy = new Subject<void>();

  readonly dataSource = new MatTableDataSource<InvoicesDataTableItem>();
  readonly displayedColumns: string[] = [
    'invoice_number',
    'type',
    'client_name',
    'client_tax_number',
    'debtor_name',
    'debtor_tax_number',
    'amount',
    'due_date_at',
    'issued_at',
  ];
  readonly invoiceTypeLabel: Record<string, string | null> = {
    SZ: 'Számla',
    HS: 'Helyesbítő számla',
    SS: 'Sztornó számla',
    JS: 'Jóváíró számla',
    ES: 'Előlegszámla',
    VS: 'Végszámla',
    D: 'Díjbekérő',
    SL: 'Szállítólevél',
  };

  @ViewChild(MatSort, { static: true }) sort?: MatSort;

  readonly invoiceNumber = new FormControl<string | null>(null);
  readonly clientName = new FormControl<string | null>(null);
  readonly clientTaxNumber = new FormControl<string | null>(null);
  readonly debtorName = new FormControl<string | null>(null);
  readonly debtorTaxNumber = new FormControl<string | null>(null);

  pageSize: number = 5;
  pageIndex: number = 0;
  length?: number | null = null;
  readonly loading = signal(false);

  expandedElement?: InvoicesDataTableItem;

  get expandedDetails(): GetInvoiceDetailsResponse | undefined {
    return this.expandedElement
      ? this.invoiceDetails.get(this.expandedElement)
      : undefined;
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.invoiceNumber.value ||
      this.clientName.value ||
      this.clientTaxNumber.value ||
      this.debtorName.value ||
      this.debtorTaxNumber.value
    );
  }

  private get sortBy() {
    return this.sort?.active;
  }
  private get sortDirection() {
    return this.sort?.direction;
  }

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;

    if (this.sort) {
      if ('sort_by' in queryParams) {
        const sortBy = queryParams.sort_by;
        const sortDirection = queryParams.sort_direction;

        if (
          this.displayedColumns.includes(sortBy) &&
          (!sortDirection || ['asc', 'desc'].includes(sortDirection))
        ) {
          this.sort.sort({
            disableClear: false,
            id: sortBy,
            start: sortDirection,
          });
        }
      }
    }

    if (queryParams.invoice_number) {
      this.invoiceNumber.patchValue(queryParams.invoice_number);
    }
    if (queryParams.client_name) {
      this.clientName.patchValue(queryParams.client_name);
    }
    if (queryParams.client_tax_number) {
      this.clientTaxNumber.patchValue(queryParams.client_tax_number);
    }
    if (queryParams.debtor_name) {
      this.debtorName.patchValue(queryParams.debtor_name);
    }
    if (queryParams.debtor_tax_number) {
      this.debtorTaxNumber.patchValue(queryParams.debtor_tax_number);
    }

    this.setDataSubject
      .pipe(
        takeUntil(this.destroy),
        filter(() => !this.loading()),
        debounceTime(500),
      )
      .subscribe({
        next: async () => await this.setData(),
      });

    merge(
      this.invoiceNumber.valueChanges,
      this.clientName.valueChanges,
      this.clientTaxNumber.valueChanges,
      this.debtorName.valueChanges,
      this.debtorTaxNumber.valueChanges,
    )
      .pipe(
        takeUntil(this.destroy),
        debounceTime(500),
        distinctUntilChanged(),
        startWith(null),
      )
      .subscribe({
        next: async () => {
          this.pageIndex = 0;
          this.cursor = null;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              search: null,
              invoice_number: this.invoiceNumber.value || null,
              client_name: this.clientName.value || null,
              client_tax_number: this.clientTaxNumber.value || null,
              debtor_name: this.debtorName.value || null,
              debtor_tax_number: this.debtorTaxNumber.value || null,
            },
            queryParamsHandling: 'merge',
          });
          await this.setData();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  clearAllFilters(): void {
    this.invoiceNumber.setValue(null);
    this.clientName.setValue(null);
    this.clientTaxNumber.setValue(null);
    this.debtorName.setValue(null);
    this.debtorTaxNumber.setValue(null);
  }

  sortChange() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        sort_by: this.sortBy,
        sort_direction: this.sortDirection,
      },
      queryParamsHandling: 'merge',
    });

    this.pageIndex = 0;
    this.cursor = null;

    this.setDataSubject.next();
  }

  pageChange(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.cursor = null;
      this.pageIndex = 0;
    } else {
      if (
        !event.previousPageIndex ||
        event.previousPageIndex < event.pageIndex
      ) {
        this.cursor = this.next;
      } else {
        this.cursor = this.prev;
      }
    }
    if (event.pageIndex === 0) {
      this.cursor = null;
    }

    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    this.setDataSubject.next();
  }

  async updateExpandedElement(invoice: InvoicesDataTableItem): Promise<void> {
    if (invoice === this.expandedElement) {
      this.expandedElement = undefined;
      return;
    }

    this.expandedElement = invoice;
    try {
      this.invoiceDetails.set(
        invoice,
        await this.szamlazzhuService.getInvoiceDetails(invoice.id),
      );
    } catch (error) {
      console.error('Error while getting invoice details', error);
    }
  }

  private async setData() {
    try {
      this.loading.set(true);

      const result = await this.szamlazzhuService.getInvoicesDataTable({
        sort_by: this.sortBy,
        sort_direction: this.sortDirection || null,
        invoice_number: this.invoiceNumber.value || null,
        client_name: this.clientName.value || null,
        client_tax_number: this.clientTaxNumber.value || null,
        debtor_name: this.debtorName.value || null,
        debtor_tax_number: this.debtorTaxNumber.value || null,
        cursor: this.cursor,
        per_page: this.pageSize,
      });
      this.dataSource.data = result.data;

      this.next = result.pagination.next;
      this.prev = result.pagination.prev;
      this.length = Infinity;
      if (!this.next) {
        this.length = this.pageSize * this.pageIndex + result.data.length;
      }
    } catch (error) {
      console.error('Error while getting invoices', error);
    } finally {
      this.loading.set(false);
    }
  }
}
